import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LandingNavigation from '../components/LandingNavigation';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

const TermsOfService = () => {
    const lastUpdated = 'February 7, 2026';

    return (
        <div className="legal-page">
            <LandingNavigation />

            <div className="legal-container">
                <Link to="/" className="legal-back-link">
                    <ArrowLeft size={16} />
                    Back to Home
                </Link>

                <h1 className="legal-title">Terms of Service</h1>
                <p className="legal-updated">Last updated: {lastUpdated}</p>

                <div className="legal-content">
                    <section className="legal-section">
                        <h2>1. Acceptance of Terms</h2>
                        <p>
                            Welcome to Vitisiti. These Terms of Service ("Terms") govern your access to and use
                            of the Vitisiti platform, including our website, applications, and services
                            (collectively, the "Service"). By accessing or using Vitisiti, you agree to be
                            bound by these Terms.
                        </p>
                        <p>
                            If you do not agree to these Terms, you may not access or use the Service. If you
                            are using the Service on behalf of an organization, you represent and warrant that
                            you have the authority to bind that organization to these Terms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>2. Description of Service</h2>
                        <p>
                            Vitisiti is an AI-powered creative generation and marketing platform that provides:
                        </p>
                        <ul>
                            <li>AI-generated creative content for advertising and marketing</li>
                            <li>Brand management and asset organization tools</li>
                            <li>Content scheduling and publishing capabilities</li>
                            <li>Integration with third-party advertising platforms</li>
                            <li>Analytics and performance insights</li>
                            <li>Campaign management and optimization tools</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>3. Account Registration</h2>
                        <p>
                            To use certain features of the Service, you must create an account. When creating
                            an account, you agree to:
                        </p>
                        <ul>
                            <li>Provide accurate, current, and complete information</li>
                            <li>Maintain and promptly update your account information</li>
                            <li>Keep your password secure and confidential</li>
                            <li>Accept responsibility for all activities under your account</li>
                            <li>Notify us immediately of any unauthorized access</li>
                        </ul>
                        <p>
                            You must be at least 16 years old to create an account and use the Service.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>4. Acceptable Use</h2>
                        <p>You agree not to use the Service to:</p>
                        <ul>
                            <li>Violate any applicable laws, regulations, or third-party rights</li>
                            <li>Create, upload, or distribute harmful, offensive, or illegal content</li>
                            <li>Infringe on intellectual property rights of others</li>
                            <li>Attempt to gain unauthorized access to the Service or other systems</li>
                            <li>Interfere with or disrupt the Service or servers</li>
                            <li>Use the Service for spam, phishing, or fraudulent activities</li>
                            <li>Reverse engineer, decompile, or attempt to extract source code</li>
                            <li>Use automated systems to access the Service without permission</li>
                            <li>Create misleading or deceptive advertising content</li>
                            <li>Violate advertising platform policies (Meta, Google, etc.)</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>5. User Content</h2>

                        <h3>5.1 Your Content</h3>
                        <p>
                            You retain ownership of content you upload to the Service ("User Content").
                            By uploading User Content, you grant Vitisiti a worldwide, non-exclusive,
                            royalty-free license to use, store, and process your content solely for the
                            purpose of providing the Service.
                        </p>

                        <h3>5.2 AI-Generated Content</h3>
                        <p>
                            Content generated by our AI tools is provided for your use in accordance with
                            these Terms. You are responsible for reviewing and ensuring AI-generated content
                            complies with applicable laws and platform policies before use.
                        </p>

                        <h3>5.3 Content Responsibility</h3>
                        <p>
                            You are solely responsible for your User Content and any content you publish
                            through the Service. Vitisiti does not endorse or guarantee the accuracy of
                            any user-generated or AI-generated content.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>6. Third-Party Integrations</h2>
                        <p>
                            The Service integrates with third-party platforms including but not limited to:
                        </p>
                        <ul>
                            <li>Meta (Facebook, Instagram) for advertising and content publishing</li>
                            <li>Google for analytics and advertising</li>
                            <li>Other social media and advertising platforms</li>
                        </ul>
                        <p>
                            Your use of these integrations is subject to the respective platform's terms of
                            service and policies. We are not responsible for the actions, content, or policies
                            of third-party platforms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Intellectual Property</h2>

                        <h3>7.1 Our Property</h3>
                        <p>
                            The Service, including its design, features, and content (excluding User Content),
                            is owned by Vitisiti and protected by intellectual property laws. You may not copy,
                            modify, distribute, or create derivative works without our written permission.
                        </p>

                        <h3>7.2 Feedback</h3>
                        <p>
                            If you provide feedback or suggestions about the Service, you grant us the right
                            to use such feedback without restriction or compensation.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Payment and Billing</h2>
                        <ul>
                            <li>Paid features require a valid payment method</li>
                            <li>Subscription fees are billed in advance on a recurring basis</li>
                            <li>All fees are non-refundable unless otherwise stated</li>
                            <li>We may change pricing with reasonable notice</li>
                            <li>You are responsible for applicable taxes</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>9. Termination</h2>
                        <p>
                            You may terminate your account at any time through your account settings or by
                            contacting us. We may suspend or terminate your access to the Service if you
                            violate these Terms or for any other reason with or without notice.
                        </p>
                        <p>
                            Upon termination, your right to use the Service ceases immediately. We may delete
                            your account data in accordance with our Privacy Policy.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>10. Disclaimers</h2>
                        <p>
                            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                            EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
                            ERROR-FREE, OR SECURE.
                        </p>
                        <p>
                            AI-generated content may contain errors or inaccuracies. You are responsible for
                            reviewing all content before use. We do not guarantee specific results from using
                            the Service.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>11. Limitation of Liability</h2>
                        <p>
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, VITISITI SHALL NOT BE LIABLE FOR ANY
                            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
                            OF PROFITS, REVENUE, DATA, OR USE, ARISING FROM YOUR USE OF THE SERVICE.
                        </p>
                        <p>
                            OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM THESE TERMS OR THE SERVICE SHALL
                            NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>12. Indemnification</h2>
                        <p>
                            You agree to indemnify and hold Vitisiti harmless from any claims, damages, losses,
                            or expenses (including legal fees) arising from your use of the Service, your
                            User Content, or your violation of these Terms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>13. Changes to Terms</h2>
                        <p>
                            We may modify these Terms at any time. We will notify you of material changes by
                            posting the updated Terms on the Service and updating the "Last updated" date.
                            Your continued use of the Service after changes constitutes acceptance of the
                            modified Terms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>14. Governing Law</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with applicable laws,
                            without regard to conflict of law principles. Any disputes arising from these Terms
                            or the Service shall be resolved through binding arbitration or in the courts of
                            competent jurisdiction.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>15. Miscellaneous</h2>
                        <ul>
                            <li>These Terms constitute the entire agreement between you and Vitisiti</li>
                            <li>Our failure to enforce any right does not waive that right</li>
                            <li>If any provision is found unenforceable, the remaining provisions remain in effect</li>
                            <li>You may not assign these Terms without our consent</li>
                            <li>We may assign our rights and obligations under these Terms</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>16. Contact Us</h2>
                        <p>
                            If you have questions about these Terms of Service, please contact us at:
                        </p>
                        <div className="legal-contact">
                            <p><strong>Vitisiti</strong></p>
                            <p>Email: legal@vitisiti.com</p>
                            <p>For general inquiries: support@vitisiti.com</p>
                        </div>
                    </section>
                </div>
            </div>

            <LandingFooter />
        </div>
    );
};

export default TermsOfService;
